/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
/**
 * DbConfigService should be used as part of the root module to setup DB configurations and not
 * post that.
 */
import { Injectable } from '@angular/core';
import { WindowConfig, AppWindowNames } from '../window/window.config';
import {
    DbConfig,
    DbStoreDefinition,
    DbUpgradeCallback,
    DbStoreDefinitionMap,
    DbStoreDefault
} from './models/db.models';

@Injectable({
    providedIn: 'root'
})
export class DbConfigService {
    private readonly dbName: AppWindowNames;
    private readonly globalStoreDefMap: DbStoreDefinitionMap;
    private readonly userStoreDefMap: DbStoreDefinitionMap;

    get globalStores() {
        return this.globalStoreDefMap;
    }

    get userStores() {
        return this.userStoreDefMap;
    }

    get appDbName() {
        return this.dbName;
    }

    constructor(windowConfig: WindowConfig) {
        this.dbName = windowConfig.name;
        this.globalStoreDefMap = new Map();
        this.userStoreDefMap = new Map();
    }

    private defineStore(dbConfig: DbConfig, storeDefs: DbStoreDefinitionMap): DbConfigService {
        const storeName = dbConfig.storeName;
        if (storeDefs.has(storeName)) {
            throw new Error(`Error: store name already exists: ${storeName}`);
        }
        dbConfig.name = this.dbName;

        let storeDefault: DbStoreDefault;
        if (dbConfig.hasOwnProperty('storeDefault')) {
            storeDefault = dbConfig.storeDefault;
            delete dbConfig.storeDefault;
        }

        const storeDef: DbStoreDefinition = {
            config: dbConfig,
            upgrades: new Map(),
            storeDefault
        };

        storeDefs.set(storeName, storeDef);
        return this;
    }

    private defineUpgrade(
        storeName: string,
        version: number,
        callback: DbUpgradeCallback,
        storeDefMap: DbStoreDefinitionMap
    ): DbConfigService {
        const storeDef = storeDefMap.get(storeName);
        storeDef.upgrades.set(version, callback);
        storeDef.upgradeTargetVersion = version;
        return this;
    }

    /**
     * Defines an application-wide datastore. The datastore doesn't get created and upgraded until
     * it is used for the first time.
     *
     * @example
     * dbService.init('dbName')
     *   .defineStore({
     *     storeName: 'global',
     *     description: 'This is a description of the datastore'
     *   })
     * @returns a 'this' reference for chaining
     */
    defineGlobalStore(config: DbConfig): DbConfigService {
        return this.defineStore.call(this, config, this.globalStoreDefMap);
    }

    /**
     * Defines a user-specific datastore.
     *
     * @see {$link defineGlobalStore}
     */
    defineUserStore(config: DbConfig): DbConfigService {
        return this.defineStore.call(this, config, this.userStoreDefMap);
    }

    /**
     * Defines an upgrade for an application-wide datastore. Upgrades must be defined in order,
     * sequentially.
     *
     * @example
     * // In this example, we are defining an app-wide datastore and an upgrade path. If the version
     * // on the disk is 0 or not defined, both upgrade paths will run. If it's 1, only the second
     * // will run, and for 2, neither will run. All upgrade paths must return an observable in order
     * // to be resolved properly before the DB is accessed.
     * dbService.init('dbName')
     *   .defineStore({storeName: 'test'})
     *   .upgradeStore('test', 1, function (dataStore) {
     *     return dataStore.setItem('key', 'value');
     *   })
     *   .upgradeStore('test', 2, function (dataStore) {
     *     return dataStore.setItem('key2', 'value2');
     *   });
     *
     * @returns a 'this' reference for chaining
     */
    upgradeGlobalStore(storeName: string, version: number, callback: DbUpgradeCallback): DbConfigService {
        return this.defineUpgrade.call(this, storeName, version, callback, this.globalStoreDefMap);
    }

    upgradeUserStore(storeName: string, version: number, callback: DbUpgradeCallback): DbConfigService {
        return this.defineUpgrade.call(this, storeName, version, callback, this.userStoreDefMap);
    }
}
